import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as CompanyActions from './company-info.actions';
import { CompanyStoreService } from './company-info.service';
import { ArkikAlertService } from 'src/app/shared/components/arkik-alert/arkik-alert.service';
import { TranslationService } from 'src/app/core/services/common/translation.service';
import { map, switchMap, catchError, withLatestFrom } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectCompanyByGuid } from './company-info.selectors';
import { ManageIdbService } from 'src/app/idb/manage-idb.service';
import { IdbCatalogName } from 'src/app/idb/idb-catalog-name.enum';
import { ICompanyModel } from 'src/app/core/models/admin/company/company.model';

@Injectable()
export class CompanyInfoEffects {
  constructor(
    private actions$: Actions,
    private companyService: CompanyStoreService,
    private alert: ArkikAlertService,
    private translation: TranslationService,
    private store: Store,
    private idb: ManageIdbService,
  ) {}

  loadCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyActions.loadCompany),
      withLatestFrom(this.store), // obtenemos el store completo
      switchMap(([action, storeState]: any) => {
        const companyGuid = action.payload.CompanyGuid;
        // Usamos el selector directamente sobre el storeState
        const company = selectCompanyByGuid(companyGuid).projector(storeState.companyInfo);
        if (company) {
          return of(CompanyActions.loadCompanySuccess({ company }));
        }

        return from(this.idb.select(IdbCatalogName.CompanyInfo)).pipe(
          switchMap((cachedCompany: ICompanyModel) => {
            if (cachedCompany?.CompanyGuid?.toUpperCase() === companyGuid?.toUpperCase()) {
              return of(CompanyActions.loadCompanySuccess({ company: cachedCompany }));
            }

            return this.companyService.getCompanyInfo(action.payload).pipe(
              switchMap((response) => from(this.idb.saveCompanyInfo(response)).pipe(map(() => response))),
              map((response) => CompanyActions.loadCompanySuccess({ company: response })),
              catchError((e) => {
                this.alert.error(e?.error?.message || this.translation.pt('views.globals.an-error-occurred'));
                return of(
                  CompanyActions.loadCompanyFailure({
                    error: e?.error?.message || this.translation.pt('views.globals.an-error-occurred'),
                  }),
                );
              }),
            );
          }),
        );
      }),
    ),
  );

  refreshCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyActions.refreshCompany),
      map(() => CompanyActions.resetCompany()),
    ),
  );
}
